import Link from 'next/link'
import styled, { css } from 'styled-components'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { colors, fonts, media } from 'shared/lib'
import { Raffle, RaffleDetail } from 'shared/types/raffles'
import { FC_STORES } from 'shared/constants/raffleStores'
import { RaffleCountdown } from './RaffleCountdown'
import { t, getLocale } from 'localization'
import { formatRaffleDateLong, getRaffleDisplayStatus } from './raffleUtils'
import { SpinnerIcon } from 'shared/components/Spinner'

const TP = 'shared.components.Raffles.RafflePanel'

interface IRafflePanelProps {
  raffle: RaffleDetail | Raffle
  variant: 'detail' | 'pdp'
  hasEntered?: boolean
  isCheckingEntry?: boolean
  collapsed?: boolean
  onEnterRaffle?: () => void
  onNotifyMe?: () => void
  onToggle?: () => void
}

export const RafflePanel = ({
  raffle,
  variant,
  hasEntered = false,
  isCheckingEntry = false,
  collapsed = false,
  onEnterRaffle,
  onNotifyMe,
  onToggle,
}: IRafflePanelProps) => {
  const { endDate, startDate, stores, slug, displayTimeZone } = raffle
  const displayStatus = getRaffleDisplayStatus(raffle)
  const raffleLocale = getLocale()

  // PDP variant — compact bar rendered on product detail pages.
  // Header copy is the same for both states ("IN-STORE DRAWING"); the
  // countdown target and CTA copy adapt to status:
  //   - active   → "Closes in [countdown to endDate]"   + CTA "ENTER NOW"
  //   - upcoming → "Opens in  [countdown to startDate]" + CTA "VIEW NOW"
  // (The red blinking dot is hidden in the upcoming state — the raffle
  // isn't actively open yet.)
  if (variant === 'pdp') {
    const isUpcoming = displayStatus === 'upcoming'
    const countdownPrefix = isUpcoming
      ? t(`${TP}.opensIn`, 'Opens in')
      : t(`${TP}.closesIn`, 'Closes in')
    const countdownTarget = isUpcoming ? startDate : endDate
    const ctaLabel = isUpcoming
      ? t(`${TP}.viewNow`, 'View Now')
      : t(`${TP}.enterNow`, 'Enter Now')
    const handlePdpCtaClick = () => {
      sendTrackingEvent('DRAWING_PDP_PANEL_TAP', {
        product_template_slug: slug,
        cta_label: ctaLabel,
        drawing_status: displayStatus,
      })
    }
    return (
      <PdpWrapper data-qa="raffle-panel-pdp">
        <PdpContent>
          <PdpStatusRow>
            {!isUpcoming && <PdpDot />}
            <PdpStatusText>
              {t(`${TP}.inStoreDrawing`, 'In-Store Drawing')}
            </PdpStatusText>
          </PdpStatusRow>
          <PdpCountdown>
            {countdownPrefix} <RaffleCountdown targetDate={countdownTarget} />
          </PdpCountdown>
        </PdpContent>
        <Link href={`/drawings/${slug}`} passHref legacyBehavior>
          <PdpButton
            data-qa="raffle-panel-view-raffle"
            onClick={handlePdpCtaClick}
          >
            {ctaLabel}
          </PdpButton>
        </Link>
      </PdpWrapper>
    )
  }

  // Detail variant — closed/completed state shows the status label, the
  // ended date, and a CTA to the product detail page in a lightweight
  // off-white panel.
  if (displayStatus === 'closed') {
    return (
      <ClosedWrapper data-qa="raffle-panel-detail">
        <ClosedRow>
          <ClosedInfo>
            <ClosedStatusText>
              {t(`${TP}.raffleClosed`, 'Drawing Closed')}
            </ClosedStatusText>
            <ClosedEndedText>{formatRaffleDateLong(endDate, displayTimeZone, raffleLocale)}</ClosedEndedText>
          </ClosedInfo>
          <ClosedCtaButton href={`/${slug}`} data-qa="raffle-panel-view-product">
            {t(`${TP}.viewProduct`, 'View Product')}
          </ClosedCtaButton>
        </ClosedRow>
      </ClosedWrapper>
    )
  }

  // Detail variant — the full card shown in the left/right sidebar.
  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Don't toggle if clicking on a button or link (CTA buttons)
    if ((e.target as HTMLElement).closest('button, a')) return
    onToggle?.()
  }

  return (
    <DetailWrapper
      data-qa="raffle-panel-detail"
      onClick={handleWrapperClick}
      $hasToggle={!!onToggle}
    >
      <ExpandableDetails $collapsed={collapsed}>
        <Details>
          <DetailsCols>
            <DetailCol>
              <DetailLabel>
                {displayStatus === 'upcoming'
                  ? t(`${TP}.drawDateOpens`, 'Opens')
                  : displayStatus === 'active'
                    ? t(`${TP}.drawDateEnterBy`, 'Enter By')
                    : t(`${TP}.drawDateClosed`, 'Closed')}
              </DetailLabel>
              <DetailValue>
                {formatRaffleDateLong(displayStatus === 'upcoming' ? startDate : endDate, displayTimeZone, raffleLocale)}
              </DetailValue>
            </DetailCol>
            {stores.length > 0 && (
              <DetailCol>
                <DetailLabel>{t(`${TP}.availableAt`, 'Pickup Locations')}</DetailLabel>
                <DetailValueStack>
                  {stores.map((storeName) => (
                    <span key={storeName}>
                      {t(`shared.components.Raffles.stores.${FC_STORES[storeName]?.id}.city`, storeName)}
                    </span>
                  ))}
                </DetailValueStack>
              </DetailCol>
            )}
          </DetailsCols>
        </Details>
      </ExpandableDetails>

      <BottomDivider $collapsed={collapsed} />

      <Bottom>
        {displayStatus === 'active' && !hasEntered && (
          <>
            <BottomInfo>
              <StatusRow>
                <StatusDot />
                <StatusText>{t(`${TP}.raffleOpen`, 'Open')}</StatusText>
              </StatusRow>
              <Countdown>
                {t(`${TP}.closesIn`, 'Closes in')} <RaffleCountdown targetDate={endDate} />
              </Countdown>
            </BottomInfo>
            <EnterButton
              onClick={onEnterRaffle}
              disabled={isCheckingEntry}
              data-qa="raffle-panel-enter"
            >
              {isCheckingEntry && <ButtonSpinner data-qa="raffle-panel-spinner" />}
              <ButtonText $isLoading={isCheckingEntry}>
                {t(`${TP}.enterRaffle`, 'Enter Now')}
              </ButtonText>
            </EnterButton>
          </>
        )}

        {displayStatus === 'active' && hasEntered && (
          <>
            <BottomInfo>
              <StatusRow>
                <StatusDot />
                <StatusText>{t(`${TP}.raffleOpen`, 'Open')}</StatusText>
              </StatusRow>
              <Countdown>
                {t(`${TP}.closesIn`, 'Closes in')} <RaffleCountdown targetDate={endDate} />
              </Countdown>
            </BottomInfo>
            <EnteredButton disabled data-qa="raffle-panel-entered">
              {t(`${TP}.raffleEntered`, 'Entered')}
            </EnteredButton>
          </>
        )}

        {displayStatus === 'upcoming' && (
          <>
            <BottomInfo>
              <StatusRow>
                <StatusText>
                  {t(`${TP}.upcomingRaffle`, 'Coming Soon')}
                </StatusText>
              </StatusRow>
              <Countdown>
                {t(`${TP}.opensIn`, 'Opens in')} <RaffleCountdown targetDate={startDate} />
              </Countdown>
            </BottomInfo>
            <NotifyButton onClick={onNotifyMe} data-qa="raffle-panel-notify">
              {t(`${TP}.notifyMe`, 'Notify Me')}
            </NotifyButton>
          </>
        )}
      </Bottom>
    </DetailWrapper>
  )
}

/* ============================================================
   Detail variant — ports .desktop-cta / .cta-* from prototype
============================================================ */

const DetailWrapper = styled.div<{ $hasToggle?: boolean }>`
  background: ${colors.FC2_BLACK};
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 50px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  position: sticky;
  top: 80px;
  cursor: ${({ $hasToggle }) => ($hasToggle ? 'pointer' : 'default')};

  ${media.larger`
    margin-bottom: 0;
  `}

  ${media.extraLarge`
    margin-bottom: 50px;
  `}
`

/* Expandable wrapper for collapsible details section */
const ExpandableDetails = styled.div<{ $collapsed: boolean }>`
  overflow: hidden;
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  max-height: ${({ $collapsed }) => ($collapsed ? '0px' : '1000px')};
  transition: opacity 0.15s ease, max-height 0.3s ease;
`

/* Top details row: two columns on the left + "Product info" chevron
   pinned to the top-right. */
const Details = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 16px;
  gap: 12px;
`

const DetailsCols = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
`

const DetailCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DetailLabel = styled.p`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_WHITE};
  line-height: 1.2;
  white-space: nowrap;
  margin: 0;
`

const DetailValue = styled.p`
  ${fonts.IVAR_TEXT_SMALL}
  font-size: 14px;
  color: ${colors.FC2_WHITE};
  line-height: 1;
  letter-spacing: -0.14px;
  margin: 0;
`

const DetailValueStack = styled.div`
  ${fonts.IVAR_TEXT_SMALL}
  font-size: 14px;
  color: ${colors.FC2_WHITE};
  line-height: 1;
  letter-spacing: -0.14px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    display: block;
  }
`

/* Full-width divider: uses negative horizontal margin so it bleeds
   to the panel edges despite the 16px container padding. Margin-bottom
   controlled by collapsed state. */
const BottomDivider = styled.div<{ $collapsed: boolean }>`
  height: 1px;
  background: rgb(255 255 255 / 20%);
  margin: 0 -16px;
  margin-bottom: ${({ $collapsed }) => ($collapsed ? '0px' : '17px')};
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  transition: opacity 0.15s ease, margin-bottom 0.15s ease;
`

/* Bottom row: status+countdown on the left, small CTA button on right. */
const Bottom = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
`

const BottomInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
`

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  background: ${colors.FC2_RED};
  flex-shrink: 0;
  animation: fc-blink 1s steps(1, end) infinite;

  @keyframes fc-blink {
    0%, 59.9% {
      opacity: 1;
    }

    60%, 100% {
      opacity: 0;
    }
  }
`

const StatusText = styled.span`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_WHITE};
  letter-spacing: 0.2px;
  white-space: nowrap;
  text-transform: uppercase;
`

const Countdown = styled.div`
  ${fonts.BODY_TEXT}
  color: ${colors.FC2_WHITE};
  line-height: 1.2;
  white-space: nowrap;
`

const ButtonSpinner = styled(SpinnerIcon)`
  width: 16px;
  height: 16px;
  border-width: 2px;
  margin-right: 8px;
  border-color: rgb(255 255 255 / 30%);
  border-right-color: ${colors.FC2_WHITE};
`

const ButtonText = styled.span<{ $isLoading?: boolean }>`
  opacity: ${({ $isLoading }) => ($isLoading ? 0.7 : 1)};
`

/* Small CTA button — matches .desktop-cta-btn from prototype. This is
   intentionally not the 56px full-width button used inside the entry
   sheet; the panel button is compact at 12px/500 uppercase. */
const smallCtaBase = css`
  ${fonts.SUBTITLE_3}
  padding: 12px;
  border-radius: 2px;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: background-color 150ms ease, color 150ms ease, border-color 150ms ease;
`

const EnterButton = styled.button`
  ${smallCtaBase}
  background: ${colors.FC2_RED};
  color: ${colors.FC2_WHITE};
  border: 1px solid ${colors.FC2_RED};

  &:disabled {
    background: ${colors.FC2_DISABLED_GREY};
    border-color: ${colors.FC2_DISABLED_GREY};
    cursor: default;
    pointer-events: none;
  }
`

const EnteredButton = styled.button`
  ${smallCtaBase}
  background: ${colors.FC2_DARK_GREY};
  color: ${colors.FC2_LIGHT_GREY};
  border: 1px solid ${colors.FC2_DARK_GREY};
  cursor: default;
  pointer-events: none;
`

const NotifyButton = styled.button`
  ${smallCtaBase}
  background: transparent;
  color: ${colors.FC2_WHITE};
  border: 1px solid ${colors.FC2_WHITE};
`

/* ============================================================
   Closed/completed variant — lightweight off-white panel that
   replaces the full detail card once a drawing has ended. Renders
   only the status label and a CTA back to the underlying PDP.
============================================================ */

const ClosedWrapper = styled.div`
  background: ${colors.FC2_OFF_WHITE};
  border: 1px solid ${colors.FC2_LIGHTEST_GREY};
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 50px;
  width: 100%;
  position: sticky;
  top: 80px;

  ${media.larger`
    margin-bottom: 0;
  `}

  ${media.extraLarge`
    margin-bottom: 50px;
  `}
`

const ClosedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`

const ClosedInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`

const ClosedStatusText = styled.span`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_BLACK};
  letter-spacing: 0.2px;
  text-transform: uppercase;
  white-space: nowrap;
`

const ClosedEndedText = styled.span`
  ${fonts.BODY_TEXT}
  color: ${colors.FC2_BLACK};
  line-height: 1.2;
  white-space: nowrap;
`

const ClosedCtaButton = styled.a`
  ${fonts.SUBTITLE_3}
  line-height: 1.2;
  padding: 12px 16px;
  border: 1px solid ${colors.FC2_BLACK};
  border-radius: 2px;
  background: ${colors.FC2_BLACK};
  color: ${colors.FC2_WHITE};
  text-decoration: none;
  text-align: center;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
`

/* ============================================================
   PDP variant — unchanged from prior port; used on product pages.
============================================================ */

const PdpWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 16px;
  margin-bottom: 30px;
  background-color: ${colors.FC2_OFF_WHITE};
  border: 1px solid ${colors.FC2_LIGHTEST_GREY};
  border-radius: 16px;
  overflow: hidden;
`

const PdpContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  height: 40px;
`

const PdpStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const PdpDot = styled.span`
  width: 6px;
  height: 6px;
  background-color: ${colors.FC2_RED};
  flex-shrink: 0;
`

const PdpStatusText = styled.span`
  ${fonts.SUBTITLE_3}
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
`

const PdpCountdown = styled.span`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2;
  color: ${colors.FC2_BLACK};
  white-space: nowrap;
`

const PdpButton = styled.a`
  ${fonts.SUBTITLE_3}
  line-height: 1.2;
  padding: 12px 16px;
  border: 1px solid ${colors.FC2_BLACK};
  border-radius: 2px;
  background: ${colors.FC2_WHITE};
  color: ${colors.FC2_BLACK};
  text-decoration: none;
  text-align: center;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
`
